import { useEffect, useRef, useState } from 'react'
import { useClickOutsideAndEscape } from 'hooks'
import { fetchMasterDataDetails } from 'utils/api/masterDataApi'
import { CatalogueProductOption } from 'types/bundleProduct'
import { SearchIcon } from 'components/icons'
import ProductImage from 'components/masterData/productImage'
import { getProductDisplayName, getProductPackInfo } from './utils'

type Props = {
  placeholder?: string
  excludeIds?: string[]
  error?: string
  disabled?: boolean
  onSelect: (option: CatalogueProductOption) => void
}

const SEARCH_DEBOUNCE_MS = 400

const ProductSearchInput = ({
  placeholder = 'Search products by barcode or description',
  excludeIds = [],
  error,
  disabled,
  onSelect,
}: Props) => {
  const [isVisible, setIsVisible] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [options, setOptions] = useState<CatalogueProductOption[]>([])
  const [loading, setLoading] = useState(false)

  const ref = useRef<HTMLDivElement | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  useClickOutsideAndEscape({
    ref,
    callback: () => setIsVisible(false),
  })

  useEffect(() => {
    if (!isVisible) return

    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const { data } = await fetchMasterDataDetails({
          limit: 20,
          page: 1,
          search: searchQuery || undefined,
        })
        const docs: CatalogueProductOption[] = data?.docs || []
        setOptions(docs.filter((doc) => !excludeIds.includes(doc._id)))
      } catch (error) {
        setOptions([])
      } finally {
        setLoading(false)
      }
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(debounceRef.current)
  }, [searchQuery, isVisible, excludeIds])

  const handleSelect = (option: CatalogueProductOption) => {
    onSelect(option)
    setIsVisible(false)
    setSearchQuery('')
  }

  return (
    <div className="relative w-full max-w-lg" ref={ref}>
      <div
        className={`flex h-11 items-center gap-2.5 rounded-xl border bg-white px-3.5 shadow-sm transition-shadow focus-within:shadow-md ${
          error
            ? 'border-error'
            : 'border-gray-200 focus-within:border-gray-400'
        }`}
      >
        <span className="h-4 w-4 shrink-0 text-gray-400">
          <SearchIcon />
        </span>
        <input
          type="text"
          disabled={disabled}
          value={searchQuery}
          onFocus={() => setIsVisible(true)}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            setIsVisible(true)
          }}
          placeholder={placeholder}
          className="h-full w-full bg-transparent text-sm text-primary outline-none placeholder:text-gray-400 disabled:cursor-not-allowed disabled:text-gray-400"
        />
      </div>

      {isVisible && (
        <div className="absolute z-10 mt-2 w-full overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl">
          <div className="max-h-80 overflow-auto p-1.5">
            {loading ? (
              <div className="px-3 py-4 text-center text-sm text-gray-400">
                Searching...
              </div>
            ) : options.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-gray-400">
                {searchQuery
                  ? 'No matching products found'
                  : 'Start typing to search the catalogue'}
              </div>
            ) : (
              options.map((option) => {
                const packInfo = getProductPackInfo(option)

                return (
                  <button
                    type="button"
                    key={option._id}
                    onClick={() => handleSelect(option)}
                    className="flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
                  >
                    <ProductImage
                      src={option.image ?? null}
                      alt={getProductDisplayName(option)}
                      fallbackText=""
                      size={36}
                      className="shrink-0 border-gray-100"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-primary">
                        {getProductDisplayName(option)}
                      </span>
                      <span className="block truncate text-xs text-gray-500">
                        {[option.itemNo, option._id, packInfo]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}

      {error && <p className="mt-1 text-xs text-error">{error}</p>}
    </div>
  )
}

export default ProductSearchInput
